import { Link } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, Bot, Cpu, Wallet, ShieldCheck, Coins, Zap, Globe, Users,
  ArrowUpRight, FileText, Server
} from 'lucide-react';

const FAQ = [
  {
    q: 'What is getbitsy.ai?',
    a: 'getbitsy.ai is the Agentic Hospitality OS — a direct booking engine and AI-operated PMS for independent hotels. It replaces middleman OTAs (Booking.com, Expedia) with five autonomous AI agents (Bitsy, Marina, Sage, Argus, Hermes) and a 5%+1% commission model where 1% flows back to operators as on-chain rewards.',
  },
  {
    q: 'What is the BHERMES Protocol?',
    a: 'BHERMES is the open Web3 hospitality protocol that powers getbitsy.ai. It coordinates Soulbound Reputation Tokens, cross-chain $HERMES tokens (Base, Ethereum, Arbitrum), DAO governance, and IoT-verified billing — independently of any single platform.',
  },
  {
    q: 'How is getbitsy a Direct Booking Engine?',
    a: 'Hotels publish rooms directly on getbitsy.ai. Guests book and pay the hotel directly with no OTA in the middle. Hotels keep ~94% of revenue (vs ~85% on Booking.com). Stripe handles fiat payments; $HERMES handles on-chain settlements.',
  },
  {
    q: 'What Hotel AI Agents come with getbitsy?',
    a: 'Five agents: Bitsy (24/7 guest concierge with vendor-aware product chat), Marina (staff operations and restock intelligence), Sage (revenue and dynamic pricing), Argus (AI-guided hotel onboarding and photo coverage), and Hermes (autonomous hotel verification with auto-approve / review / reject scoring).',
  },
  {
    q: 'How does Middleman-Free Hospitality work?',
    a: 'Hotels list directly on getbitsy.ai. Guests find hotels via the BHERMES World Map (3D digital-twin metaverse) or partner channels. AI agents handle booking, check-in, concierge, and post-stay loyalty — eliminating both the OTA fee and most third-party SaaS subscriptions.',
  },
  {
    q: 'What is the 1% protocol reward?',
    a: 'Of every booking commission, 1% is pooled and redistributed monthly to high-performing hotels and vendors based on a transparent, score-weighted formula. It turns your platform fee into your operating revenue.',
  },
  {
    q: 'Is the booking data on-chain?',
    a: 'Hot booking data lives in MongoDB for sub-second performance. Settlement seals (SHA-256 hashes of monthly tax reports) are notarized on Base Sepolia for tamper-proof audit. Reputation, token mints, and DAO votes are fully on-chain.',
  },
  {
    q: 'Can my hotel use getbitsy without crypto?',
    a: 'Yes. Hotels can run getbitsy.ai with Stripe-only payments and ignore the $HERMES side entirely. The 1% rewards pool waits in your operator wallet and can be cashed out, ignored, or staked at your discretion.',
  },
];

const AGENTS = [
  {
    id: 'bitsy',
    name: 'Bitsy',
    role: 'Guest concierge',
    color: 'amber',
    desc: 'GPT-5.2-powered 24/7 in-room and metaverse concierge. Vendor-aware: knows what mattress is in each room, can recommend a linen brand, and embeds shop links in chat. Includes age-gated ordering and ChromaDB-backed long-term memory for returning guests.',
    surfaces: ['Metaverse Viewer', 'Smart Tray PWA', 'Smart Fridge PWA', 'Bitsy 3D avatar'],
  },
  {
    id: 'marina',
    name: 'Marina',
    role: 'Operations & restock',
    color: 'cyan',
    desc: 'Staff-facing dashboard for restock intelligence, housekeeping, and vendor product lifecycle. Generates per-room briefings so staff can answer guest product questions.',
    surfaces: ['Marina Dashboard', 'Restock Reconciliation', 'Vendor Analytics'],
  },
  {
    id: 'sage',
    name: 'Sage',
    role: 'Revenue intelligence',
    color: 'emerald',
    desc: 'Dynamic pricing engine, A/B pricing experiments, vendor BI, and reorder predictions. Generates GPT-5.2 strategic recommendations from cross-hotel data.',
    surfaces: ['Vendor Analytics', 'A/B Pricing', 'Sage Reports'],
  },
  {
    id: 'argus',
    name: 'Argus',
    role: 'Onboarding & photos',
    color: 'teal',
    desc: 'getbitsy.ai\'s onboarding agent. Conversational hotel registration with split-panel chat, AI photo enhancement (4 size variants + WebP + auto-categorization via GPT-5.2 Vision).',
    surfaces: ['/register-hotel', 'Photo Management', 'Hotel Coverage'],
  },
  {
    id: 'hermes',
    name: 'Hermes',
    role: 'Protocol verification',
    color: 'orange',
    desc: 'Autonomous hotel-legitimacy scoring across 4 dimensions (business, location, online presence, photos). Auto-approves at score ≥85, flags 50–84 for human review, auto-rejects below 50. On approval, mints HOTEL SBT and triggers tokenomics.',
    surfaces: ['/hermes/verify-hotel', 'Hermes Stats', 'Argus Admin'],
  },
];

const ENDPOINTS = [
  { method: 'GET', path: '/api/public/protocol-stats', desc: 'Live protocol stats (hotels, bookings, SBTs, revenue)' },
  { method: 'GET', path: '/api/hotels/public/list', desc: 'All publicly listed hotels with hero photos' },
  { method: 'GET', path: '/api/trusted-guest/public/{handle}', desc: 'Zero-PII Trusted Guest badge' },
  { method: 'GET', path: '/api/venue-partners/stats/{handle}', desc: 'Aggregated venue trust count' },
  { method: 'POST', path: '/api/registration/start', desc: 'Begin AI-guided hotel registration with Argus' },
  { method: 'POST', path: '/api/hermes/verify-hotel', desc: 'Trigger Hermes autonomous verification' },
  { method: 'GET', path: '/api/faucet/info', desc: 'Testnet $HERMES drip info (Base Sepolia)' },
  { method: 'GET', path: '/api/governance/params/all', desc: 'DAO-governed protocol parameters' },
];

export default function DocsPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const agentServices = AGENTS.map((a) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${a.name} — ${a.role}`,
    serviceType: 'Hotel AI Agent',
    description: a.desc,
    provider: { '@type': 'Organization', name: 'getbitsy.ai', url: 'https://getbitsy.ai' },
    areaServed: 'Worldwide',
  }));

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="docs-page">
      <SEO
        title="Documentation — getbitsy.ai Agentic Hospitality OS"
        description="Technical reference for getbitsy.ai: BHERMES Protocol, Hotel AI Agents (Bitsy, Marina, Sage, Argus, Hermes), Direct Booking Engine architecture, API endpoints, and middleman-free hospitality model."
        canonical="https://getbitsy.ai/docs"
        schema={[faqSchema, ...agentServices]}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">
          ← getbitsy.ai
        </Link>
        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">
          TECHNICAL DOCS
        </Badge>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 space-y-16">
        {/* Hero */}
        <section>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">DOCUMENTATION HUB</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            getbitsy.ai — the <span className="text-amber-400">Agentic Hospitality OS</span>
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-3xl leading-relaxed">
            A direct booking engine and AI-operated PMS for independent hotels. Powered by
            the <Link to="/protocol" className="text-amber-400 hover:underline">BHERMES Protocol</Link> —
            five autonomous Hotel AI Agents, Soulbound Reputation, IoT-verified billing, and
            cross-chain $HERMES rewards. Middleman-free.
          </p>

          <div className="grid md:grid-cols-4 gap-3 mt-8">
            <QuickLink to="/why-getbitsy" icon={Zap} label="Why getbitsy" />
            <QuickLink to="/protocol" icon={Globe} label="Live protocol stats" />
            <QuickLink href="/hermes-protocol-whitepaper.pdf" icon={FileText} label="Whitepaper PDF" external />
            <QuickLink href="/llms.txt" icon={Bot} label="llms.txt manifest" external />
          </div>
        </section>

        {/* Architecture */}
        <Section title="Architecture" icon={Cpu}>
          <p className="text-zinc-400">
            getbitsy.ai is a multi-tenant SaaS layered on top of the open BHERMES Protocol. Hot data
            lives in MongoDB; settlement, reputation, and governance live on-chain.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <ArchCard title="Frontend" items={['React 19', 'Tailwind + Shadcn UI', 'Reown AppKit (embedded wallets)', 'Three.js metaverse']} />
            <ArchCard title="Backend" items={['FastAPI + MongoDB', 'GPT-5.2 (Emergent LLM)', 'WebSocket hubs (live presence)', 'Stripe Connect + 0xSplits']} />
            <ArchCard title="Blockchain" items={['Base · Ethereum · Arbitrum', 'LayerZero V2 OFT ($HERMES)', 'ERC-4337 (gasless wallets)', 'Pimlico session keys']} />
          </div>
        </Section>

        {/* Hotel AI Agents */}
        <Section title="Hotel AI Agents" icon={Bot}>
          <p className="text-zinc-400">
            Five agents do the work of three full-time staff and a revenue manager combined.
          </p>
          <div className="space-y-4 mt-4">
            {AGENTS.map((a) => (
              <Card key={a.id} id={a.id} className="bg-zinc-900 border-zinc-800 scroll-mt-20" data-testid={`agent-${a.id}`}>
                <CardContent className="p-6 grid md:grid-cols-[180px_1fr] gap-6 items-start">
                  <div>
                    <div className={`w-12 h-12 rounded-xl bg-${a.color}-500/15 flex items-center justify-center mb-2`}>
                      <Bot className={`w-6 h-6 text-${a.color}-400`} />
                    </div>
                    <p className="text-2xl font-semibold">{a.name}</p>
                    <p className="text-xs text-zinc-500 tracking-widest">{a.role.toUpperCase()}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-zinc-300 leading-relaxed">{a.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {a.surfaces.map((s) => (
                        <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400">{s}</span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* Token Economy */}
        <Section title="Token Economy ($HERMES)" icon={Coins}>
          <div className="grid md:grid-cols-2 gap-4">
            <Bullet><b>Halving model</b>: 100K $HERMES per hotel in Era 1, halves every 108 hotels (1.08B cap)</Bullet>
            <Bullet><b>5% + 1% commission</b>: 5% to platform, 1% to monthly score-weighted reward pool</Bullet>
            <Bullet><b>Cross-chain</b>: Native on Base, bridges to Ethereum + Arbitrum via LayerZero V2 OFT</Bullet>
            <Bullet><b>Vendor + creator economy</b>: 25K per vendor, 1% protocol cut, staged minting over 90 days</Bullet>
            <Bullet><b>Faucet</b>: 10 $HERMES / wallet / 24h on Base Sepolia for builders (<Link to="/faucet" className="text-amber-400 hover:underline">/faucet</Link>)</Bullet>
            <Bullet><b>Governance</b>: 8 protocol parameters DAO-governed (<Link to="/governance/parameters" className="text-amber-400 hover:underline">/governance/parameters</Link>)</Bullet>
          </div>
        </Section>

        {/* SBT Reputation */}
        <Section title="Soulbound Reputation (SBT)" icon={ShieldCheck}>
          <p className="text-zinc-400">
            Privacy-first, multi-role identity that follows guests across hotels. Zero PII stored —
            only bucketed age (18/21/25), behavior signals, and badges.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Bullet><b>PERSON SBT</b> (one per wallet): guest, staff, creator roles co-existing on a single soulbound token</Bullet>
            <Bullet><b>HOTEL SBT</b> (one per property): nature_score, hotel_score, staking_tier, total_bookings</Bullet>
            <Bullet><b>Trusted Guest Badge</b>: shareable zero-PII URL (<Link to="/verify-badge" className="text-amber-400 hover:underline">/verify-badge</Link>) for door staff</Bullet>
            <Bullet><b>Vendor SBT</b>: tracks product quality, delivery reliability, QR engagement, tenure</Bullet>
          </div>
        </Section>

        {/* IoT */}
        <Section title="IoT — Material of Attested Truth (M.O.A.T.)" icon={Wallet}>
          <p className="text-zinc-400">
            Physical hardware (load cells, weight sensors, mattress telemetry) feeds verified
            ground-truth into autonomous smart-contract settlements. No human input, no disputes.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <Bullet><b>Smart Tray</b>: weight-validated minibar restock + per-slot reconciliation</Bullet>
            <Bullet><b>Smart Fridge</b>: dual-rail checkout (Stripe + on-chain $HERMES intent)</Bullet>
            <Bullet><b>Bryte Mattress</b>: occupancy-surge per-night leasing with cross-checked PMS telemetry</Bullet>
          </div>
        </Section>

        {/* API */}
        <Section title="Public API endpoints" icon={Server}>
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            {ENDPOINTS.map((e, i) => (
              <div key={i} className={`grid grid-cols-[80px_280px_1fr] gap-4 px-4 py-3 text-sm ${i % 2 ? 'bg-zinc-900/30' : ''}`}>
                <span className="text-emerald-400 font-mono text-xs">{e.method}</span>
                <code className="text-zinc-200 text-xs">{e.path}</code>
                <span className="text-zinc-500 text-xs">{e.desc}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* FAQ */}
        <Section title="Frequently Asked" icon={Users}>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-medium text-zinc-100 list-none flex justify-between items-center">
                  {f.q}
                  <ArrowUpRight className="w-4 h-4 text-zinc-500 transition-transform group-open:rotate-45" />
                </summary>
                <p className="text-zinc-400 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* CTAs */}
        <section className="grid md:grid-cols-3 gap-4 pt-4">
          <Link to="/why-getbitsy" className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-6 hover:bg-amber-500/10 transition" data-testid="cta-why">
            <p className="text-amber-400 text-xs tracking-widest mb-1">FOR HOTELIERS</p>
            <p className="text-lg font-semibold">Why getbitsy vs SaaS?</p>
            <p className="text-zinc-400 text-sm mt-1">ROI calculator and competitor comparison.</p>
          </Link>
          <Link to="/register-hotel" className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6 hover:bg-emerald-500/10 transition" data-testid="cta-list">
            <p className="text-emerald-400 text-xs tracking-widest mb-1">LIST YOUR HOTEL</p>
            <p className="text-lg font-semibold">AI-guided onboarding</p>
            <p className="text-zinc-400 text-sm mt-1">Argus walks you through it in 4 minutes.</p>
          </Link>
          <Link to="/protocol" className="rounded-xl border border-zinc-700 bg-zinc-900/50 p-6 hover:bg-zinc-900 transition" data-testid="cta-protocol">
            <p className="text-zinc-400 text-xs tracking-widest mb-1">FOR DEVELOPERS</p>
            <p className="text-lg font-semibold">BHERMES Protocol</p>
            <p className="text-zinc-400 text-sm mt-1">Live stats, contracts, governance.</p>
          </Link>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · Middleman-Free Hospitality
      </footer>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-amber-400" />
        </div>
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ArchCard({ title, items }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-5">
        <p className="text-xs tracking-widest text-zinc-500 mb-2">{title.toUpperCase()}</p>
        <ul className="space-y-1.5">
          {items.map((i) => <li key={i} className="text-sm text-zinc-200">{i}</li>)}
        </ul>
      </CardContent>
    </Card>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <BookOpen className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
      <p className="text-sm text-zinc-300 leading-relaxed">{children}</p>
    </div>
  );
}

function QuickLink({ to, href, icon: Icon, label, external }) {
  const Comp = external ? 'a' : Link;
  const props = external ? { href, target: '_blank', rel: 'noreferrer' } : { to };
  return (
    <Comp {...props} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 flex items-center gap-2 hover:bg-zinc-900 hover:border-amber-500/40 transition">
      <Icon className="w-4 h-4 text-amber-400" />
      <span className="text-sm text-zinc-200">{label}</span>
    </Comp>
  );
}
